// src/components/ParticipantTracker/utils/dateUtils.ts

import { parse, isValid, fromUnixTime } from 'date-fns';

export const TIMEZONE = 'America/New_York';
export const TIMEZONE_LABEL = 'ET';

// Native timezone-aware formatters
const createFormatter = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    ...options,
  });
};

// Pre-configured formatters for common patterns
const formatters = {
  date: createFormatter({ month: 'short', day: 'numeric', year: 'numeric' }),
  dateShort: createFormatter({ month: 'short', day: 'numeric' }),
  dateWithDay: createFormatter({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
  time: createFormatter({ hour: 'numeric', minute: '2-digit', hour12: true }),
  dateTime: createFormatter({ month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
  full: createFormatter({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
};

const etDateFormatter = createFormatter({
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const getETDateParts = (date: Date): { year: number; month: number; day: number } => {
  const parts = etDateFormatter.formatToParts(date);
  let year = 0;
  let month = 0;
  let day = 0;

  parts.forEach(part => {
    if (part.type === 'year') year = parseInt(part.value, 10);
    if (part.type === 'month') month = parseInt(part.value, 10);
    if (part.type === 'day') day = parseInt(part.value, 10);
  });

  return { year, month, day };
};

export const getETCalendarDayKey = (date: Date): string => {
  const { year, month, day } = getETDateParts(date);
  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-');
};

const etCalendarNoonUtcMs = (date: Date): number => {
  const { year, month, day } = getETDateParts(date);
  return Date.UTC(year, month - 1, day, 12);
};

export const differenceInETCalendarDays = (dateLeft: Date, dateRight: Date): number => {
  return Math.round((etCalendarNoonUtcMs(dateLeft) - etCalendarNoonUtcMs(dateRight)) / 86400000);
};

export const parseUtcDateTime = (value: string | null | undefined): Date | null => {
  if (!value || value.startsWith('0000-')) return null;

  const trimmed = value.trim();
  const mysqlDateTimeMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (mysqlDateTimeMatch) {
    const [, y, m, d, hh = '00', mm = '00', ss = '00'] = mysqlDateTimeMatch;
    const parsed = new Date(Date.UTC(
      parseInt(y, 10),
      parseInt(m, 10) - 1,
      parseInt(d, 10),
      parseInt(hh, 10),
      parseInt(mm, 10),
      parseInt(ss, 10)
    ));
    return isValid(parsed) ? parsed : null;
  }

  const parsed = new Date(trimmed);
  return isValid(parsed) ? parsed : null;
};

/**
 * Anchor a parsed date-only value to noon EST (UTC-5).
 * Spreadsheet dates are assumed US Eastern Time. Using noon EST ensures
 * the calendar date stays correct when displayed in ET regardless of
 * the browser's local timezone (noon EST = 17:00 UTC, safe for any offset).
 */
const toNoonET = (year: number, month: number, day: number): Date => {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return new Date(`${year}-${mm}-${dd}T12:00:00-05:00`);
};

export const parseApiDate = (dateInput: number | string | null | undefined): Date | null => {
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;
  let parsedDate: Date | null = null;

  if (typeof dateInput === 'number') {
    if (isFinite(dateInput) && dateInput > 0) {
      // Re-anchor to noon ET like the string paths below. Schedule anchors
      // arrive as ET-midnight timestamps, but date-fns day arithmetic
      // (addDays et al.) preserves the *browser's* local wall clock — from a
      // midnight instant, any derived label that sits across a US DST
      // transition from the anchor renders one day early in timezones that
      // don't follow US DST rules (UTC VMs, etc.). Noon leaves ~12h of slack
      // in both directions, so the ET calendar day survives in any timezone.
      const { year, month, day } = getETDateParts(fromUnixTime(dateInput));
      parsedDate = toNoonET(year, month, day);
    }
  } else if (typeof dateInput === 'string') {
    const dateOnlyString = dateInput.split(' ')[0];
    try {
      // Try yyyy-MM-dd first (most common from API/spreadsheets)
      let dt = parse(dateOnlyString, 'yyyy-MM-dd', new Date());
      if (isValid(dt)) {
        parsedDate = toNoonET(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
      } else {
        // Try dd-MMM-yy (e.g. "15-Mar-25")
        dt = parse(dateOnlyString, 'dd-MMM-yy', new Date(2000, 0, 1));
        if (isValid(dt)) {
          parsedDate = toNoonET(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
        } else {
          // Last resort: native Date constructor, then re-anchor to noon ET
          dt = new Date(dateOnlyString);
          if (isValid(dt)) {
            parsedDate = toNoonET(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
          }
        }
      }
    } catch { /* Ignore parsing errors */ }
  }
  return isValid(parsedDate) ? parsedDate : null;
};

export const formatDisplayDate = (date: Date | null | undefined): string => {
  if (!date || !isValid(date)) return 'N/A';
  try {
    return formatters.date.format(date);
  } catch {
    return 'Invalid Date';
  }
};

export const formatDisplayDateShort = (date: Date | null | undefined): string => {
  if (!date || !isValid(date)) return 'N/A';
  try {
    return formatters.dateShort.format(date);
  } catch {
    return 'Invalid Date';
  }
};

export const formatTimestamp = (unixTimestamp: number | null | undefined): string => {
  if (typeof unixTimestamp !== 'number' || !isFinite(unixTimestamp) || unixTimestamp <= 0) {
    return 'Invalid Time';
  }
  try {
    return formatters.date.format(fromUnixTime(unixTimestamp));
  } catch {
    return 'Format Error';
  }
};

export const formatTimestampWithDay = (unixTimestamp: number | null | undefined): string => {
  if (typeof unixTimestamp !== 'number' || !isFinite(unixTimestamp) || unixTimestamp <= 0) {
    return 'Invalid Time';
  }
  try {
    return `${formatters.full.format(fromUnixTime(unixTimestamp))} ${TIMEZONE_LABEL}`;
  } catch {
    return 'Format Error';
  }
};

// Flexible formatter for specific patterns
export const formatTimestampET = (
  unixTimestamp: number,
  type: 'date' | 'dateShort' | 'dateWithDay' | 'time' | 'dateTime' | 'full' = 'dateTime'
): string => {
  try {
    const date = fromUnixTime(unixTimestamp);
    return formatters[type].format(date);
  } catch {
    return 'Invalid date';
  }
};

// Get just the time portion
export const formatTimeET = (unixTimestamp: number): string => {
  try {
    return formatters.time.format(fromUnixTime(unixTimestamp));
  } catch {
    return 'Invalid time';
  }
};
